// Admin users tab. One row per user, sorted by most recently active.
// Read-only at v1 — eventual ops (impersonate, force-logout, mark
// admin) plug in here as row actions later.

import { useEffect, useState } from 'react'
import { api, type AdminUserRow } from '../api'

export function AdminUsersView() {
  const [rows, setRows] = useState<AdminUserRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const r = await api.listAdminUsers()
        if (!cancelled) setRows(r.users)
      } catch (e) {
        if (!cancelled) setError((e as Error).message)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  if (error) {
    return (
      <div
        style={{
          background: 'var(--red-soft)',
          border: '1px solid var(--red)',
          padding: '6px 10px',
          borderRadius: 4,
          color: 'var(--red)',
          fontSize: 12,
        }}
      >
        {error}
      </div>
    )
  }
  if (!rows) {
    return <div style={{ color: 'var(--ink-3)', fontSize: 12 }}>loading…</div>
  }

  return (
    <div style={{ fontSize: 13, color: 'var(--ink-1)' }}>
      <div
        style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 12 }}
      >
        {rows.length} user{rows.length === 1 ? '' : 's'} · sorted by last
        activity
      </div>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 12,
        }}
      >
        <thead>
          <tr
            style={{
              borderBottom: '1px solid var(--rule-s)',
              textAlign: 'left',
            }}
          >
            <th style={th}>user</th>
            <th style={th}>tenant</th>
            <th style={th}>joined</th>
            <th style={th}>last active</th>
            <th style={thNum}>calls</th>
            <th style={thNum}>in / out</th>
            <th style={thNum}>errors</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td style={td}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>
                    {r.email ?? r.github_login ?? r.id.slice(0, 8)}
                  </span>
                  {r.is_admin && (
                    <span
                      style={{
                        background: 'var(--paper-2)',
                        color: 'var(--ink-2)',
                        padding: '1px 5px',
                        borderRadius: 3,
                        fontSize: 10,
                        textTransform: 'uppercase',
                        letterSpacing: '.04em',
                      }}
                    >
                      admin
                    </span>
                  )}
                </div>
                {r.github_login && r.email && (
                  <div
                    style={{
                      color: 'var(--ink-3)',
                      fontSize: 11,
                      marginTop: 2,
                    }}
                  >
                    @{r.github_login}
                  </div>
                )}
              </td>
              <td style={td}>{r.tenant_name}</td>
              <td style={td}>{fmtDate(r.created_at)}</td>
              <td style={td}>
                {r.last_activity ? fmtRelative(r.last_activity) : '—'}
              </td>
              <td style={tdNum}>{fmtNum(r.calls)}</td>
              <td style={tdNum}>
                {fmtTokens(r.input_tokens)} / {fmtTokens(r.output_tokens)}
              </td>
              <td
                style={{
                  ...tdNum,
                  color: r.errors > 0 ? 'var(--red, #c54)' : undefined,
                }}
              >
                {fmtNum(r.errors)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Formatting ─────────────────────────────────────────────────────

function fmtNum(n: number): string {
  return n.toLocaleString()
}

function fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toLocaleString()
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString()
}

function fmtRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  if (ms < 60_000) return 'just now'
  const min = Math.floor(ms / 60_000)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const day = Math.floor(hr / 24)
  if (day < 30) return `${day}d ago`
  return new Date(iso).toLocaleDateString()
}

const th: React.CSSProperties = {
  padding: '6px 8px',
  fontWeight: 500,
  color: 'var(--ink-3)',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '.04em',
}

const thNum: React.CSSProperties = { ...th, textAlign: 'right' }

const td: React.CSSProperties = {
  padding: '8px 8px',
  verticalAlign: 'top',
  borderBottom: '1px solid var(--rule-xs, #eee)',
}

const tdNum: React.CSSProperties = {
  ...td,
  textAlign: 'right',
  fontFeatureSettings: '"tnum"',
}
